import { twMerge } from "tailwind-merge";
import { Card, LoadingScreen } from "@growtherapy/sprout-ui";
import {
  InputControlsWrapper as InputControls,
  InputControlsWrapperProps,
} from "../InputControls";
import {
  ProviderAvatarSectionWrapper as ProviderAvatarSection,
  ProviderAvatarSectionProps,
} from "../ProviderAvatarSection";
import {
  ProviderLoginWrapper as ProviderLogin,
  ProviderLoginBaseProps,
} from "../ProviderLogin";
import { FC } from "react";
import {
  LoginSectionWrapper as LoginSection,
  LoginSectionProps,
} from "../auth/LoginSection";

import { useRequiredAgreements } from "@grow-therapy-team/seedling-components";
import { useFeatureGate } from "@statsig/react-bindings";
import { useAuth0 } from "@auth0/auth0-react";
import { requiredAgreementsEnabled } from "../../../statsig/gates";
import {
  RequiredAgreementsWrapper as RequiredAgreements,
  RequiredAgreementsWrapperProps,
} from "../required-agreements/RequiredAgreements";
import { useGetClientRequiresAuthedIntroRoom } from "../auth/useClientRequiresAuthedIntroRoom";
export interface MinimalPageContentProps {
  className?: string;
  inputControlsComponent: FC<InputControlsWrapperProps>;
  providerLoginComponent: FC<ProviderLoginBaseProps>;
  providerAvatarSectionComponent: FC<ProviderAvatarSectionProps>;
  loginSectionComponent: FC<LoginSectionProps>;
  requiredAgreementsComponent: FC<RequiredAgreementsWrapperProps>;
  requiredAgreementsProps: RequiredAgreementsWrapperProps;
  submitRequiredAgreements: (onSuccessfulSubmission: () => void) => void;
  hasPendingRequiredAgreements: boolean;
  showRequiredAgreements: boolean;
  loading: boolean;
}

export function MinimalPageContent({
  className,
  inputControlsComponent: InputControlsComponent,
  providerLoginComponent: ProviderLoginComponent,
  providerAvatarSectionComponent: ProviderAvatarComponent,
  loginSectionComponent: LoginSectionComponent,
  requiredAgreementsComponent: RequiredAgreementsComponent,
  requiredAgreementsProps,
  submitRequiredAgreements,
  hasPendingRequiredAgreements,
  showRequiredAgreements,
  loading,
}: MinimalPageContentProps) {
  if (loading) {
    return <LoadingScreen />;
  }
  return (
    <Card
      className={twMerge(
        "self-center sm:w-[34rem] z-10 sm:p-0 flex flex-col items-center w-full py-6 sm:py-12 gap-8 border-0 sm:border",
        className,
      )}
      data-testid="client-intro-state--control"
    >
      <ProviderAvatarComponent className="px-6 sm:px-12" />
      <LoginSectionComponent className="px-6 sm:px-12" />
      {showRequiredAgreements && (
        <RequiredAgreementsComponent
          {...requiredAgreementsProps}
          className="mx-6 sm:mx-12"
        />
      )}
      <InputControlsComponent
        className="flex flex-col items-center gap-8 px-6 sm:px-12"
        submitRequiredAgreements={submitRequiredAgreements}
        hasPendingRequiredAgreements={hasPendingRequiredAgreements}
      />
      <ProviderLoginComponent className="px-6 sm:px-12" />
    </Card>
  );
}

export type MinimalPageContentWrapperProps = Pick<
  MinimalPageContentProps,
  "className"
>;

export function MinimalPageContentWrapper({
  className,
}: MinimalPageContentWrapperProps) {
  const { data, loading: clientRequiresAuthedIntroRoomLoading } =
    useGetClientRequiresAuthedIntroRoom();
  const clientUserShortId =
    data?.clientRequiresAuthedIntroRoom?.clientUserShortId;
  const isMinor = data?.clientRequiresAuthedIntroRoom?.isMinor ?? false;

  const { value: isRequiredAgreementsEnabled } = useFeatureGate(
    requiredAgreementsEnabled,
  );
  const { isAuthenticated } = useAuth0();
  const showRequiredAgreements = isAuthenticated && isRequiredAgreementsEnabled;

  const {
    missingConsentTypes,
    clientUserWithParentGuardianInformation,
    isSubmitting,
    isFamilyAccount,
    requiresSelfSignedConsents,
    setFieldValue,
    formFields,
    submitConsents,
    loading,
  } = useRequiredAgreements(
    clientUserShortId ?? "",
    showRequiredAgreements && !!clientUserShortId,
  );

  const hasPendingRequiredAgreements =
    missingConsentTypes && missingConsentTypes.length > 0;

  return (
    <MinimalPageContent
      className={className}
      inputControlsComponent={InputControls}
      providerLoginComponent={ProviderLogin}
      providerAvatarSectionComponent={ProviderAvatarSection}
      loginSectionComponent={LoginSection}
      requiredAgreementsComponent={RequiredAgreements}
      submitRequiredAgreements={submitConsents}
      hasPendingRequiredAgreements={hasPendingRequiredAgreements}
      requiredAgreementsProps={{
        missingConsentTypes,
        clientUserWithParentGuardianInformation,
        isSubmitting,
        isFamilyAccount,
        requiresSelfSignedConsents,
        setFieldValue,
        formFields,
      }}
      showRequiredAgreements={
        showRequiredAgreements && hasPendingRequiredAgreements && !isMinor
      }
      loading={loading || clientRequiresAuthedIntroRoomLoading}
    />
  );
}
